<script setup lang="ts">
// Profile picture (fetched with auth, cached, rendered in greyscale) or a generative mark:
// a mirrored bitmap (for spaces, in a palette colour), or a QR code of a link (`qr`, e.g. a
// space's link in its QR view). Optional online marker.
import { computed, ref, watch } from 'vue'
import { forTheme } from '../lib/color'
import { identicon, markColor } from '../lib/pattern'
import { activeTheme } from '../lib/theme'
import { qrMatrix, qrPath } from '../lib/qr'
import { parsePixels } from '../lib/pixels'
import { conformIcon } from '../lib/color'
import PixelArt from './PixelArt.vue'
import { useSession } from '../stores/session'

const props = withDefaults(
  defineProps<{
    id: string
    name: string
    size?: number
    square?: boolean
    presence?: boolean
    qr?: string
    pixels?: string
  }>(),
  { size: 40, square: false, presence: false },
)
const session = useSession()
// Photos only where the server allows them; otherwise the design or the generated mark.
const version = computed(() => (session.profilePhotos ? session.profiles.get(props.id)?.avatar : undefined))
const url = ref<string | null>(null)
const online = computed(() => props.presence && session.online.has(props.id))
const cells = computed(() => identicon(props.id))
// Spaces' generated marks take a colour from the palette, so they're easy to tell apart.
const tint = computed(() => {
  const c = props.square ? markColor(props.id, session.palette) : undefined
  return c && forTheme(c, activeTheme.value === 'dark')
})
const code = computed(() => (props.qr ? qrMatrix(props.qr) : null))
// A designed picture: a space symbol passed in, or the person's profile design.
// Drawn in the current palette: colours an admin removed show as their nearest replacement.
const design = computed(() => {
  const d = parsePixels(props.pixels ?? session.profiles.get(props.id)?.pixels)
  return d && conformIcon(d, session.palette)
})
/**
 * QR codes are drawn at a whole number of pixels per module (plus a one-module white
 * margin, which scanners need to find the corners), with no border: fractional modules
 * blur together on screen.
 */
const qrPx = computed(() => {
  const n = (code.value?.size ?? 0) + 2
  return n * Math.max(1, Math.floor(props.size / n))
})

watch(
  [() => props.id, version],
  async ([id, v]) => {
    url.value = v ? await loadAvatar(id, v, session.engine?.token ?? '') : null
  },
  { immediate: true },
)
</script>

<script lang="ts">
import { apiUrl } from '../platform'

const cache = new Map<string, Promise<string | null>>()

/** Avatar image as a blob URL, cached per user+version. */
function loadAvatar(id: string, version: number, token: string): Promise<string | null> {
  const key = `${id}:${version}`
  let p = cache.get(key)
  if (!p) {
    p = fetch(apiUrl(`/users/${encodeURIComponent(id)}/avatar?v=${version}`), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => (r.ok ? URL.createObjectURL(await r.blob()) : null))
      .catch(() => null)
    cache.set(key, p)
  }
  return p
}
</script>

<template>
  <!-- Dark on light in every theme: many cameras can't read inverted codes. -->
  <div
    v-if="code && !url"
    class="relative flex shrink-0 items-center justify-center select-none"
    :style="{ width: `${size}px`, height: `${size}px` }"
    :title="name"
  >
    <svg
      :width="qrPx"
      :height="qrPx"
      :viewBox="`-1 -1 ${code.size + 2} ${code.size + 2}`"
      shape-rendering="crispEdges"
      aria-hidden="true"
      data-testid="avatar-qr"
      :data-qr="qr"
    >
      <rect x="-1" y="-1" :width="code.size + 2" :height="code.size + 2" fill="#fff" />
      <path :d="qrPath(code)" fill="#000" />
    </svg>
  </div>
  <div v-else class="relative shrink-0" :style="{ width: `${size}px`, height: `${size}px` }">
    <!-- No fill behind the pixels: they sit straight on whatever is behind (e.g. a conversation's gradient) -->
    <div class="h-full w-full overflow-hidden select-none" :title="name" aria-hidden="true">
      <img v-if="url" :src="url" alt="" class="h-full w-full object-cover grayscale" data-testid="avatar-img" />
      <PixelArt v-else-if="design" :icon="design" />
      <svg v-else viewBox="-1 -1 7 7" class="h-full w-full" shape-rendering="crispEdges">
        <template v-for="(on, i) in cells" :key="i">
          <rect
            v-if="on"
            :x="i % 5"
            :y="Math.floor(i / 5)"
            width="1"
            height="1"
            :class="tint ? '' : 'fill-text-primary'"
            :style="tint ? { fill: tint } : undefined"
          />
        </template>
      </svg>
    </div>
    <span
      v-if="online"
      class="absolute -right-2 bottom-0 h-1.5 w-1.5 bg-voice"
      data-testid="online-dot"
      aria-label="online"
    />
  </div>
</template>
